import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import { postJson } from '../../lib/api';


const InterestPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: ''
  });

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('acdcco-popup-seen');
    const lastShown = localStorage.getItem('acdcco-popup-last-shown');
    
    if (!hasSeenPopup) {
      // Show popup after 10 seconds for first-time visitors
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else if (lastShown) {
      const daysSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      
      // Show popup again after 7 days
      if (daysSinceLastShown > 7) {
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 30000); // Show after 30 seconds for returning users
        
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('acdcco-popup-seen', 'true');
    localStorage.setItem('acdcco-popup-last-shown', Date.now().toString());
  };

  const handleMaybeLater = () => {
    setIsVisible(false);
    localStorage.setItem('acdcco-popup-last-shown', Date.now().toString());
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await postJson('/newsletter-subscriptions/', {
        email: formData.email,
        name: formData.name, // The backend model doesn't have name, but we can update it or just send it if we add it. 
        interest: formData.interest
      });

      alert('Thank you for your interest! We\'ll be in touch soon.');
      setIsVisible(false);
      localStorage.setItem('acdcco-popup-seen', 'true');
      localStorage.setItem('acdcco-popup-subscribed', 'true');
      localStorage.setItem('acdcco-popup-last-shown', Date.now().toString());
      setFormData({ name: '', email: '', interest: '' });
    } catch (error) {
      alert(error.message || 'Failed to submit interest. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            {/* Popup Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Join Our Community!
                </h2>
                <p className="text-gray-600">
                  Interested in empowering Asian American communities through education and business support?
                </p>
              </div>

              {/* Interest Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What interests you most?
                  </label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select an option</option>
                    <option value="education">Education Programs</option>
                    <option value="business">Business Consulting</option>
                    <option value="courses">Technology/Business Courses</option>
                    <option value="events">Community Events</option>
                    <option value="volunteering">Volunteering</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button type="submit" className="flex-1">
                    Count Me In!
                  </Button>
                  <button
                    type="button"
                    onClick={handleMaybeLater}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Maybe Later
                  </button>
                </div>
              </form>

              {/* Benefits */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Get updates on courses, events, and community resources. No spam, unsubscribe anytime.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InterestPopup;